import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HTMLAttributes, HTMLProps } from 'react';
import { useDispatch } from 'react-redux';

import classNames from 'classnames';

import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { Button as PrimeButton, ButtonProps } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

import type {
  ColumnDef,
  PaginationState,
  RowSelectionOptions,
  PaginationOptions,
  RowSelectionState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faSquare } from '@fortawesome/pro-solid-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import { Pagination } from 'Components/Pagination';
import { EmptyStatus } from 'Components/EmptyStatus';

import { renderTableHeaderCells } from 'Components/Table/utils';

import type { GetRowKey, ReactTableProps } from 'Components/Table/types';
import type { AnyObject } from 'types';

import { globalFilters, setGlobalFilters } from 'Redux/actions';

import './Table.scss';

function IndeterminateCheckbox({
  indeterminate,
  className,
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  const disabled = rest?.disabled ?? false;

  return (
    <div
      className={classNames('table-checkbox', {
        'table-checkbox-disabled': disabled,
      })}
    >
      <input type="checkbox" ref={ref} className={classNames(className)} {...rest} />
      {rest.checked && <FontAwesomeIcon icon={faCheck} color="#FFFFFF" />}
      {!rest.checked && indeterminate && (
        <FontAwesomeIcon icon={faSquare} style={{ fontSize: 14 }} color="#007b94" />
      )}
    </div>
  );
}

const ReactTable = <RecordType extends AnyObject = AnyObject>({
  label = 'items',
  data,
  columns,
  loading,
  components,
  title,
  onRow,
  onHeaderRow,
  rowKey = 'id',
  rowDisabled,
  rowSelection,
  emptyText,
  sortConfig,
  pagination: tablePagination,
  rounded = true,
  className,
  showHeader = true,
}: ReactTableProps<RecordType>) => {
  const dispatch = useDispatch();

  const selectAllDataRef = useRef<Menu>(null);
  const menuRootWrapperRef = useRef<HTMLDivElement>(null);

  const getRowKey = useMemo<GetRowKey<RecordType>>(() => {
    if (typeof rowKey === 'function') return rowKey;

    return (record: RecordType) => record?.[rowKey as string];
  }, [rowKey]);

  const selectAllDataMenuItems = useMemo<MenuItem[]>(
    () => [
      {
        label: `${rowSelection?.isSelectedAllData ? 'Unselect' : 'Select'} all ${
          tablePagination?.total
        } ${label}`,
        command: () => rowSelection?.onSelectAllData?.(rowSelection?.isSelectedAllData ? [] : data),
      },
    ],
    [rowSelection?.isSelectedAllData, rowSelection?.onSelectAllData, tablePagination?.total]
  );

  const toggleAllDataSelect: ButtonProps['onClick'] = event => {
    selectAllDataRef?.current?.toggle(event);
  };

  const updateOutState = (selectedKeys: RowSelectionState) => {
    const keys = Object.keys(selectedKeys);
    rowSelection?.onChange?.(
      keys.map(key => (!Number.isNaN(Number(key)) ? Number(key) : key)),
      data.filter(row => keys.includes(String(getRowKey(row))))
    );
  };

  const getSelectableColumn = useCallback((): ColumnDef<RecordType>[] => {
    if (rowSelection) {
      if (!rowSelection.hideSelectAll)
        return [
          {
            id: 'select',
            size: rowSelection?.onSelectAllData ? 66 : 52,
            enableResizing: false,
            header: ({ table }) => {
              return (
                <FlexBox alignItems="center" gap="0.25rem">
                  <IndeterminateCheckbox
                    {...{
                      checked: table.getIsAllRowsSelected(),
                      indeterminate: table.getIsSomePageRowsSelected(),
                      onChange: table.getToggleAllRowsSelectedHandler(),
                      className: 'beewo-table-selection-column',
                    }}
                  />
                  {rowSelection?.onSelectAllData && (
                    <div className="select-all-toggle-wrapper" ref={menuRootWrapperRef}>
                      <Menu
                        id="popup_menu_left"
                        className="select-all-data-menu"
                        popupAlignment="left"
                        popup
                        appendTo={() =>
                          document.querySelector('.beewo-table-container') as HTMLDivElement
                        }
                        model={selectAllDataMenuItems}
                        ref={selectAllDataRef}
                      />
                      <PrimeButton
                        className="select-all-data-btn"
                        icon={
                          <FontAwesomeIcon
                            icon={faCaretDown}
                            style={{ color: '#AAB2B5', fontSize: 12 }}
                          />
                        }
                        onClick={toggleAllDataSelect}
                        aria-controls="popup_menu_left"
                        aria-haspopup
                      />
                    </div>
                  )}
                </FlexBox>
              );
            },
            cell: ({ row }) => {
              const originalNode = (
                <IndeterminateCheckbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                    className: 'beewo-table-selection-column',
                  }}
                />
              );
              return rowSelection?.checkboxRender?.(originalNode, row.original) ?? originalNode;
            },
          },
        ];

      return [
        {
          id: 'select',
          size: 52,
          enableResizing: false,
          cell: ({ row }) => {
            const originalNode = (
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                  className: 'beewo-table-selection-column',
                }}
              />
            );
            return rowSelection?.checkboxRender?.(originalNode, row.original) ?? originalNode;
          },
        },
      ];
    }
    return [];
  }, [rowSelection, tablePagination?.total]);

  const onRowSelectionChange: RowSelectionOptions<RecordType>['onRowSelectionChange'] =
    updaterOrValue => {
      const recordValues =
        rowSelection?.selectedRowKeys!.reduce<RowSelectionState>(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        ) ?? {};

      const selectedKeys = Object.keys(recordValues);
      const filteredKeys = selectedKeys.filter(
        key => data.findIndex(row => getRowKey(row) === key) === -1
      );
      const filteredRows = [...filteredKeys].reduce<RowSelectionState>(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );

      if (typeof updaterOrValue === 'function')
        updateOutState(updaterOrValue({ ...filteredRows, ...recordValues }));
      else updateOutState({ ...filteredRows, ...updaterOrValue });
    };

  const onPaginationChange: PaginationOptions['onPaginationChange'] = updaterOrValue => {
    const page = table.getState().pagination.pageIndex + 1;
    const size = table.getState().pagination.pageSize;

    if (typeof updaterOrValue === 'function') {
      const { pageIndex, pageSize } = updaterOrValue({
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: size,
      });
      tablePagination?.onChange?.(pageIndex + 1, pageSize);
    } else {
      tablePagination?.onChange?.(page, size);
    }
  };

  const tableColumns = useMemo<ColumnDef<RecordType>[]>(
    () => [...getSelectableColumn(), ...columns],
    [getSelectableColumn]
  );

  const rowSelectedKeys = useMemo(
    () =>
      rowSelection?.selectedRowKeys?.reduce<RowSelectionState>(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      ) ?? {},
    [rowSelection]
  );

  const table = useReactTable<RecordType>({
    data,
    columns: tableColumns,
    state: {
      rowSelection: rowSelectedKeys,
      sorting: sortConfig?.sorting!,
      pagination: {
        pageIndex: tablePagination?.current! - 1 ?? 0,
        pageSize: tablePagination?.pageSize ?? 10,
      },
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: tablePagination?.pageSize ?? 10,
    enableRowSelection: row => (rowDisabled ? !rowDisabled(row.original) : true),
    getRowId: row => getRowKey(row) as string,
    onRowSelectionChange,
    onSortingChange: sortConfig?.onSortingChange!,
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    dispatch(setGlobalFilters(table.setGlobalFilter));
    dispatch(globalFilters(table.getState().globalFilter));
  }, []);

  const PaginationComponent = (
    <div className={classNames('beewo-table-pagination')}>
      <Pagination
        loading={false}
        rangeOfItems={table.getState().pagination.pageSize}
        currentPage={table.getState().pagination.pageIndex + 1}
        totalItems={tablePagination?.total ?? 0}
        totalPages={tablePagination?.totalPages ?? table.getPageCount()}
        onClickPrev={() => table.previousPage()}
        onClickNext={() => table.nextPage()}
      />
    </div>
  );

  const renderPagination = useCallback(() => {
    if (
      !tablePagination ||
      data.length === 0 ||
      (tablePagination?.hideOnSinglePage && tablePagination.totalPages === 1)
    )
      return null;

    return PaginationComponent;
  }, [tablePagination]);

  return (
    <div className={classNames('beewo-table-wrapper', className)}>
      <div className="table-spin-nested-loading">
        {loading && (
          <div>
            <div className="spin-spinning">
              <FontAwesomeIcon
                icon={faSpinnerThird}
                color="#FFAB03"
                style={{ fontSize: 30, animation: 'spinner-border 0.75s linear infinite' }}
              />
            </div>
          </div>
        )}
        <div className={classNames('spin-container', loading && 'spin-blur')}>
          <div
            className={classNames('beewo-table', 'beewo-table-layout-fixed', {
              'beewo-table-rounded': rounded,
            })}
          >
            {title && <div className="beewo-table-title">{title(data)}</div>}
            <div className="beewo-table-container">
              <div className="beewo-table-content">
                <table style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    {table.getAllColumns().map(column => {
                      const isResizable = column.getCanResize();

                      const styleWidth = !isResizable ? { width: column.getSize() } : {};

                      return <col key={column.id} style={{ ...styleWidth }} />;
                    })}
                  </colgroup>
                  {showHeader && (
                    <thead className="beewo-table-thead">
                      {table.getHeaderGroups().map(headerGroup => {
                        const rowProps: HTMLAttributes<HTMLElement> = onHeaderRow
                          ? onHeaderRow(headerGroup.headers.map(header => header.column))
                          : {};

                        return (
                          <tr
                            {...rowProps}
                            key={headerGroup.id}
                            className={classNames(rowProps.className)}
                          >
                            {headerGroup.headers.map(header => {
                              return (
                                <th
                                  key={header.id}
                                  className={classNames(
                                    'beewo-table-cell',
                                    'beewo-table-cell-ellipsis',
                                    {
                                      'beewo-table-has-sorters': header.column.getCanSort(),
                                      'beewo-table-column-sort': !!header.column.getIsSorted(),
                                      'beewo-table-selection-column': header.column.id === 'select',
                                    }
                                  )}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : renderTableHeaderCells<RecordType>(
                                        header.column.getCanSort(),
                                        header
                                      )}
                                </th>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </thead>
                  )}
                  <tbody className="beewo-table-tbody">
                    {!data.length &&
                      loading &&
                      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(row => (
                        <tr key={row} data-row-key={row} className="beewo-table-row">
                          {table.getAllColumns().map(column => (
                            <td key={column.id} className="beewo-table-cell">
                              <Skeleton width="100%" height="0.5rem" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    {!data.length && !loading && (
                      <tr className="beewo-table-placeholder">
                        <td className="beew-table-cell" colSpan={tableColumns.length}>
                          {emptyText ?? <EmptyStatus />}
                        </td>
                      </tr>
                    )}
                    {table.getRowModel().rows.map(row => {
                      const rowProps: HTMLAttributes<HTMLElement> = onRow
                        ? onRow(row.original)
                        : {};
                      const RowComponent = components?.body?.row;
                      if (RowComponent) {
                        return (
                          <RowComponent
                            {...rowProps}
                            key={row.id}
                            data-row-key={row.id}
                            className={classNames('beewo-table-row', rowProps.className, {
                              'beewo-table-row-selected': row.getIsSelected(),
                              'beewo-table-row-disabled': rowDisabled && rowDisabled(row.original),
                            })}
                          >
                            {row.getVisibleCells().map(cell => {
                              return (
                                <td
                                  key={cell.id}
                                  className={classNames(
                                    'beewo-table-cell',
                                    'beewo-table-cell-ellipsis',
                                    {
                                      'beewo-table-column-sort': !!cell.column.getIsSorted(),
                                      'beewo-table-selection-column': cell.column.id === 'select',
                                    }
                                  )}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              );
                            })}
                          </RowComponent>
                        );
                      }

                      return (
                        <tr
                          {...rowProps}
                          key={row.id}
                          data-row-key={row.id}
                          className={classNames('beewo-table-row', {
                            'beewo-table-row-selected': row.getIsSelected(),
                            'beewo-table-row-disabled': rowDisabled && rowDisabled(row.original),
                          })}
                        >
                          {row.getVisibleCells().map(cell => {
                            return (
                              <td
                                key={cell.id}
                                className={classNames(
                                  'beewo-table-cell',
                                  'beewo-table-cell-ellipsis',
                                  {
                                    'beewo-table-column-sort': !!cell.column.getIsSorted(),
                                    'beewo-table-selection-column': cell.column.id === 'select',
                                  }
                                )}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default ReactTable;
